import mixpanel from "mixpanel-browser";
import { VITE_MIXPANEL_TOKEN, VITE_PROD_MODE } from "../environments";
import { IOrderItems } from "../types/order";

/**
 * Analytics utility for tracking user actions with Mixpanel
 */
export const initAnalytics = () => {
  // Only initialize if we have a token and we're in production mode
  if (VITE_MIXPANEL_TOKEN && VITE_PROD_MODE) {
    try {
      mixpanel.init(VITE_MIXPANEL_TOKEN, {
        debug: false, // Set to false in production
        track_pageview: true,
        persistence: "localStorage",
      });
      console.log("Mixpanel initialized successfully");
    } catch (error) {
      console.error("Failed to initialize Mixpanel:", error);
    }
  } else {
    // Create a mock implementation for non-production environments
    if (!VITE_PROD_MODE) {
      console.log("Mixpanel disabled in non-production environment");
    }
  }
};

/**
 * Tracking events
 */
export const EVENTS = {
  // Purchase flow events
  VIEW_PRODUCT: "View Product",
  ADD_TO_CART: "Add to Cart",
  REMOVE_FROM_CART: "Remove from Cart",
  UPDATE_CART: "Update Cart",
  VIEW_CART: "View Cart",
  BEGIN_CHECKOUT: "Begin Checkout",
  ADD_SHIPPING_INFO: "Add Shipping Info",
  ADD_PAYMENT_INFO: "Add Payment Info",
  APPLY_PROMOTION: "Apply Promotion",
  PURCHASE_COMPLETE: "Purchase Complete",
  VIEW_ORDER_DETAILS: "View Order Details",

  // Search and filter events
  SEARCH_PRODUCT: "Search Product",
  SEARCH_AUTOCOMPLETE_SELECT: "Search Autocomplete Select",
  APPLY_FILTER: "Apply Filter",
  CLEAR_FILTER: "Clear Filter",
  FILTER_CLICK: "Filter Click",
};

// For backward compatibility
export const PURCHASE_EVENTS = EVENTS;

/**
 * Identify a user in Mixpanel with their email
 * @param email User's email address
 * @param properties Additional user properties to track
 */
export const identifyUser = (
  email: string,
  properties?: Record<string, any>
) => {
  if (!email) return;
  if (!VITE_PROD_MODE || !VITE_MIXPANEL_TOKEN) return;

  try {
    // Set user identity with their email
    mixpanel.identify(email);

    // Set user properties
    mixpanel.people.set({
      $email: email,
      ...properties,
    });

    // Track identification event
    mixpanel.track("User Identified", {
      email,
    });
  } catch (error) {
    console.error("Failed to identify user in Mixpanel:", error);
  }
};

/**
 * Track a custom event in Mixpanel
 * @param eventName Name of the event to track
 * @param properties Additional properties for the event
 */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, any>
) => {
  if (!VITE_PROD_MODE || !VITE_MIXPANEL_TOKEN) return;

  try {
    mixpanel.track(eventName, properties);
  } catch (error) {
    console.error(`Failed to track event "${eventName}":`, error);
  }
};

/**
 * Track a page view in Mixpanel
 * @param pageName Name of the page being viewed
 * @param properties Additional properties for the page view
 */
export const trackPageView = (
  pageName: string,
  properties?: Record<string, any>
) => {
  try {
    mixpanel.track("Page View", {
      page: pageName,
      ...properties,
    });
  } catch (error) {
    console.error(`Failed to track page view for "${pageName}":`, error);
  }
};

/**
 * Reset the user identity in Mixpanel (for logout)
 */
export const resetIdentity = () => {
  if (!VITE_PROD_MODE || !VITE_MIXPANEL_TOKEN) return;

  try {
    mixpanel.reset();
  } catch (error) {
    console.error("Failed to reset Mixpanel identity:", error);
  }
};

/**
 * Track when a user searches for a product
 * @param searchTerm The search term entered by the user
 * @param resultsCount Number of search results (if available)
 */
export const trackProductSearch = (
  searchTerm: string,
  resultsCount?: number
) => {
  try {
    trackEvent(EVENTS.SEARCH_PRODUCT, {
      search_term: searchTerm,
      results_count: resultsCount,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Failed to track product search:", error);
  }
};

/**
 * Track when a user selects an item from search autocomplete
 * @param searchTerm The search term entered by the user
 * @param selectedItem The item selected from autocomplete
 */
export const trackSearchAutocompleteSelect = (
  searchTerm: string,
  selectedItem: string
) => {
  try {
    trackEvent(EVENTS.SEARCH_AUTOCOMPLETE_SELECT, {
      search_term: searchTerm,
      selected_item: selectedItem,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Failed to track autocomplete selection:", error);
  }
};

/**
 * Track when a user applies filters
 * @param filterType Type of filter (e.g., 'category', 'price')
 * @param filterValues Values selected for the filter
 */
export const trackApplyFilter = (
  filterType: string,
  filterValues: string[]
) => {
  try {
    trackEvent(EVENTS.APPLY_FILTER, {
      filter_type: filterType,
      filter_values: filterValues,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Failed to track filter application:", error);
  }
};

/**
 * Track when a user clears filters
 * @param filterType Type of filter being cleared (if specific) or 'all'
 */
export const trackClearFilter = (filterType: string = "all") => {
  try {
    trackEvent(EVENTS.CLEAR_FILTER, {
      filter_type: filterType,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Failed to track filter clearing:", error);
  }
};

/**
 * Track when a user views a product
 * @param productId Product ID
 * @param productName Product name
 * @param price Product price
 * @param category Product category
 */
export const trackViewProduct = (
  productId: number,
  productName: string,
  category?: string
) => {
  try {
    trackEvent(EVENTS.VIEW_PRODUCT, {
      product_id: productId,
      product_name: productName,
      category,
    });
  } catch (error) {
    console.error("Failed to track product view:", error);
  }
};

/**
 * Track when a user adds a product to cart
 * @param productId Product ID
 * @param productName Product name
 * @param price Product price
 * @param quantity Quantity added
 * @param category Product category
 */
export const trackAddToCart = (
  productId: number,
  productName: string,
  price: number,
  quantity: number,
  category?: string
) => {
  try {
    trackEvent(EVENTS.ADD_TO_CART, {
      product_id: productId,
      product_name: productName,
      price,
      quantity,
      category,
      value: price * quantity,
    });
  } catch (error) {
    console.error("Failed to track add to cart:", error);
  }
};

/**
 * Track when a user removes a product from cart
 * @param productId Product ID
 * @param productName Product name
 * @param price Product price
 * @param quantity Quantity removed
 */
export const trackRemoveFromCart = (
  productId: number,
  productName: string,
  price: number,
  quantity: number
) => {
  try {
    trackEvent(EVENTS.REMOVE_FROM_CART, {
      product_id: productId,
      product_name: productName,
      price,
      quantity,
      value: price * quantity,
    });
  } catch (error) {
    console.error("Failed to track remove from cart:", error);
  }
};

/**
 * Track when a user updates their cart (changes quantity)
 * @param productId Product ID
 * @param productName Product name
 * @param price Product price
 * @param oldQuantity Previous quantity
 * @param newQuantity New quantity
 */
export const trackUpdateCart = (
  productId: number,
  productName: string,
  price: number,
  oldQuantity: number,
  newQuantity: number
) => {
  try {
    trackEvent(EVENTS.UPDATE_CART, {
      product_id: productId,
      product_name: productName,
      price,
      old_quantity: oldQuantity,
      new_quantity: newQuantity,
      value: price * newQuantity,
    });
  } catch (error) {
    console.error("Failed to track cart update:", error);
  }
};

/**
 * Track when a user views their cart
 * @param cartItems Items in the cart
 * @param totalValue Total cart value
 * @param itemCount Number of items in cart
 */
export const trackViewCart = (
  cartItems: any[],
  totalValue: number,
  itemCount: number
) => {
  try {
    const items = cartItems.map((item) => ({
      product_id: item.productId || item.id,
      product_name: item.productName || item.name,
      price: item.price,
      quantity: item.quantity || item.qty,
    }));

    trackEvent(EVENTS.VIEW_CART, {
      items,
      total_value: totalValue,
      item_count: itemCount,
    });
  } catch (error) {
    console.error("Failed to track cart view:", error);
  }
};

/**
 * Track when a user begins checkout
 * @param cartItems Items in the checkout
 * @param totalValue Total value being checked out
 */
export const trackBeginCheckout = (cartItems: any[], totalValue: number) => {
  try {
    const items = cartItems.map((item) => ({
      product_id: item.productId || item.id,
      product_name: item.productName || item.name,
      price: item.price,
      quantity: item.quantity || item.qty,
    }));

    trackEvent(EVENTS.BEGIN_CHECKOUT, {
      items,
      total_value: totalValue,
    });
  } catch (error) {
    console.error("Failed to track checkout initiation:", error);
  }
};

/**
 * Track when a user adds shipping information
 * @param shippingMethod Shipping method selected
 * @param addressId Address ID selected
 */
export const trackAddShippingInfo = (
  shippingMethod: string,
  addressId: number
) => {
  try {
    trackEvent(EVENTS.ADD_SHIPPING_INFO, {
      shipping_method: shippingMethod,
      address_id: addressId,
    });
  } catch (error) {
    console.error("Failed to track shipping info:", error);
  }
};

/**
 * Track when a user adds payment information
 * @param paymentMethod Payment method selected
 */
export const trackAddPaymentInfo = (paymentMethod: string) => {
  try {
    trackEvent(EVENTS.ADD_PAYMENT_INFO, {
      payment_method: paymentMethod,
    });
  } catch (error) {
    console.error("Failed to track payment info:", error);
  }
};

/**
 * Track when a user applies a promotion code
 * @param code Promotion code applied
 * @param discountAmount Amount of discount
 * @param success Whether application was successful
 */
export const trackApplyPromotion = (
  code: string,
  discountAmount: number,
  success: boolean
) => {
  try {
    console.log("code, discountAmount", code, discountAmount, success);
    trackEvent(EVENTS.APPLY_PROMOTION, {
      promotion_code: code,
      discount_amount: discountAmount,
      success,
    });
  } catch (error) {
    console.error("Failed to track promotion application:", error);
  }
};

/**
 * Track when a purchase is completed
 * @param orderId Order ID
 * @param orderItems Items purchased
 * @param totalValue Total order value
 * @param discountAmount Discount amount applied
 * @param paymentMethod Payment method used
 * @param shippingMethod Shipping method used
 * @param pointsUsedAsCash Points used as cash for discount (optional)
 */
export const trackPurchaseComplete = (
  orderId: string | number,
  orderItems: IOrderItems[] | any[],
  totalValue: number,
  discountAmount: number = 0,
  paymentMethod: string,
  shippingMethod: string,
  pointsUsedAsCash?: number
) => {
  try {
    const items = orderItems.map((item) => ({
      product_id: item.productId || item.id,
      product_name: item.productName || item.name,
      price: item.price,
      quantity: item.quantity || item.qty,
    }));

    const eventData: Record<string, any> = {
      order_id: orderId,
      items,
      total_value: totalValue,
      discount_amount: discountAmount,
      revenue: totalValue - discountAmount,
      payment_method: paymentMethod,
      shipping_method: shippingMethod,
      currency: "THB",
    };

    // Add points tracking if points were used as cash
    if (pointsUsedAsCash && pointsUsedAsCash > 0) {
      eventData.points_used_as_cash = pointsUsedAsCash;
      eventData.points_discount_amount = discountAmount; // Assuming discount comes from points
    }

    trackEvent(EVENTS.PURCHASE_COMPLETE, eventData);
  } catch (error) {
    console.error("Failed to track purchase completion:", error);
  }
};

/**
 * Track when a user views their order details
 * @param orderId Order ID being viewed
 */
export const trackViewOrderDetails = (orderId: string | number) => {
  try {
    trackEvent(EVENTS.VIEW_ORDER_DETAILS, {
      order_id: orderId,
    });
  } catch (error) {
    console.error("Failed to track order details view:", error);
  }
};

export const trackFilterClick = (type: string) => {
  try {
    trackEvent(EVENTS.FILTER_CLICK, {
      type,
    });
  } catch (error) {
    console.error("Failed to track trackFilterClick:", error);
  }
};
